import { useState, useMemo } from "react";
import { Calculator, TrendingDown, Zap, Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const MIN = 10000;
const MAX = 1000000;
const SAVINGS_RATE = 0.015; // vs typical processor fees

const fmt = (n) =>
  n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round(n).toLocaleString("en-US")}`;

export const RoiCalculator = ({ className = "" }) => {
  const [volume, setVolume] = useState(120000);
  const [copied, setCopied] = useState(false);

  const { monthly, annual } = useMemo(
    () => ({ monthly: volume * SAVINGS_RATE, annual: volume * SAVINGS_RATE * 12 }),
    [volume]
  );

  const pct = ((volume - MIN) / (MAX - MIN)) * 100;

  const resultText = `Elikya ROI estimate — at ${fmt(volume)}${
    volume >= MAX ? "+" : ""
  } in monthly processing volume, we could save about ${fmt(annual)} per year (${fmt(
    monthly
  )}/mo) with same-day T+0 settlement (funds up to 2 days sooner). Explore Elikya Technology: https://elikya.tech`;

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(resultText);
    } catch (e) {
      /* clipboard may be blocked in some sandboxes */
    }
    setCopied(true);
    toast.success("Savings estimate copied — share it with your team");
    setTimeout(() => setCopied(false), 1800);
  };

  const emailResult = () => {
    const subject = encodeURIComponent("Elikya payment savings estimate");
    const body = encodeURIComponent(resultText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.success("Opening your email with the estimate");
  };

  return (
    <div
      data-testid="roi-calculator"
      className={`rounded-3xl border border-stroke bg-white p-7 shadow-float sm:p-9 ${className}`}
    >
      <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
            <Calculator className="h-4 w-4" /> ROI savings calculator
          </span>
          <h3 className="mt-3 text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            See what you could save
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-slatey">
            Drag the slider to your estimated monthly processing volume and watch your projected
            savings and settlement speed update instantly.
          </p>

          <div className="mt-7">
            <div className="flex items-end justify-between">
              <label htmlFor="roi-volume" className="text-sm font-medium text-slatey">
                Monthly processing volume
              </label>
              <span data-testid="roi-volume-label" className="text-lg font-bold text-ink tabular-nums">
                {fmt(volume)}
                {volume >= MAX && "+"}
              </span>
            </div>
            <input
              id="roi-volume"
              type="range"
              min={MIN}
              max={MAX}
              step={10000}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              data-testid="roi-slider"
              aria-label="Monthly processing volume"
              className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full outline-none"
              style={{
                background: `linear-gradient(to right, #4288CE ${pct}%, #E2E8F0 ${pct}%)`,
                accentColor: "#4288CE",
              }}
            />
            <div className="mt-2 flex justify-between text-xs text-slatey">
              <span>$10k</span>
              <span>$1M+</span>
            </div>
          </div>
        </div>

        <div className="grid gap-4">
          <div className="rounded-2xl border border-brand/20 bg-brand-tint p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-brand">
              <TrendingDown className="h-4 w-4" /> Projected annual savings
            </div>
            <p data-testid="roi-annual" className="mt-2 text-4xl font-bold tracking-tight text-ink tabular-nums">
              {fmt(annual)}
            </p>
            <p className="mt-1 text-sm text-slatey">
              about <span className="font-semibold text-ink">{fmt(monthly)}</span> every month
            </p>
          </div>
          <div className="rounded-2xl border border-stroke bg-canvas p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Zap className="h-4 w-4 text-brand" /> Settlement speed
            </div>
            <p className="mt-2 text-lg font-bold text-ink">
              T+2 <span className="text-slatey">→</span> <span className="text-brand">T+0 (same day)</span>
            </p>
            <p className="mt-1 text-sm text-slatey">Access your money up to 2 days sooner.</p>
          </div>

          <div className="flex flex-col gap-2.5 sm:flex-row" data-testid="roi-share">
            <button
              onClick={copyResult}
              data-testid="roi-copy"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand py-2.5 text-sm font-semibold text-white transition-all hover:bg-brand-hover"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy result"}
            </button>
            <button
              onClick={emailResult}
              data-testid="roi-email"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-stroke bg-white py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-brand-tint"
            >
              <Mail className="h-4 w-4 text-brand" /> Email result
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
